import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { Choice, Choices } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { api } from '@/lib/api'
import { TestPeople } from './TestPeople'
import {
  CALENDAR_STYLES,
  HOME_STYLES,
  INVITE_MODES,
  MOMENT_STYLES,
  PHOTO_STYLES,
  RECIPE_STYLES,
  SECTION_LABELS,
  SECTIONS,
  type CalendarStyle,
  type HomeStyle,
  type InviteMode,
  type MomentStyle,
  type PhotoStyle,
  type RecipeStyle,
  type Section,
  type Sections,
} from '../../shared/sections'
import { PageLoader } from '@/components/Loader'

type Account = { id: string; name: string; email: string; canInvite: boolean }
type Settings = {
  sections: Sections
  invites: InviteMode
  homeStyle: HomeStyle
  photosStyle: PhotoStyle
  momentsStyle: MomentStyle
  recipesStyle: RecipeStyle
  calendarStyle: CalendarStyle
  accounts: Account[]
}

const INVITE_CHOICES: [InviteMode, string][] = [
  ['everyone', 'Everyone'],
  ['chosen', 'Only people I pick'],
]

const STYLE_CHOICES: ['traditional' | 'modern', string][] = [
  ['traditional', 'Traditional'],
  ['modern', 'Modern'],
]

// The owner's switches, on their Preferences page and nobody else's (the server refuses anyone else too): which
// sections everyone sees, and who may invite people to a circle.
export function AdminSettings() {
  const queryClient = useQueryClient()
  const settings = useQuery({ queryKey: ['admin-settings'], queryFn: () => api<Settings>('/admin/settings') })
  // Each change shows at once; whatever the server then says is what stays.
  const show = (change: (s: Settings) => Settings) =>
    queryClient.setQueryData<Settings>(['admin-settings'], (s) => s && change(s))
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] }),
      queryClient.invalidateQueries({ queryKey: ['me'] }),
    ])
  const save = useMutation({
    mutationFn: (body: {
      sections?: Partial<Sections>
      invites?: InviteMode
      homeStyle?: HomeStyle
      photosStyle?: PhotoStyle
      momentsStyle?: MomentStyle
      recipesStyle?: RecipeStyle
      calendarStyle?: CalendarStyle
    }) => api('/admin/settings', { method: 'PATCH', body }),
    onMutate: (body) =>
      show((s) => ({
        ...s,
        sections: { ...s.sections, ...body.sections },
        invites: body.invites ?? s.invites,
        homeStyle: body.homeStyle ?? s.homeStyle,
        photosStyle: body.photosStyle ?? s.photosStyle,
        momentsStyle: body.momentsStyle ?? s.momentsStyle,
        recipesStyle: body.recipesStyle ?? s.recipesStyle,
        calendarStyle: body.calendarStyle ?? s.calendarStyle,
      })),
    onSettled: refresh,
  })
  const grant = useMutation({
    mutationFn: ({ id, canInvite }: { id: string; canInvite: boolean }) =>
      api(`/admin/invite-grants/${encodeURIComponent(id)}`, { method: 'PUT', body: { canInvite } }),
    onMutate: ({ id, canInvite }) =>
      show((s) => ({ ...s, accounts: s.accounts.map((a) => (a.id === id ? { ...a, canInvite } : a)) })),
    onSettled: refresh,
  })

  if (settings.isPending) return <PageLoader />
  if (settings.isError) return <FormError error={settings.error} />
  const { sections, invites, homeStyle, photosStyle, momentsStyle, recipesStyle, calendarStyle, accounts } =
    settings.data
  const on = SECTIONS.filter((s) => sections[s])

  return (
    <section className="flex flex-col gap-6 border-t pt-8" aria-labelledby="admin">
      <div className="flex flex-col gap-1.5">
        <h2 id="admin" className="text-title">
          Admin
        </h2>
        <p className="text-sub text-muted-foreground">Only you see this. It changes gatheredfrom for everyone.</p>
      </div>
      <Choices<Section>
        legend="Sections"
        hint="A section that’s off leaves everyone’s navigation, and its pages close. Nothing in it is deleted, and Keep a copy still includes it."
        value={on}
        options={SECTIONS.map((s) => [s, SECTION_LABELS[s]])}
        onChange={(next) =>
          save.mutate({ sections: Object.fromEntries(SECTIONS.map((s) => [s, next.includes(s)])) as Sections })
        }
      />
      <FormError error={save.error} />
      {sections.home && (
        <Choice
          legend="Home style"
          hint={
            homeStyle === 'modern'
              ? 'Home opens on what’s new since you last looked, blended together, then shelves to wander. The circle you’re in sits at the top left.'
              : 'Home opens on the welcome, a door into each section, and Lately, the way it always has.'
          }
          value={homeStyle ?? 'traditional'}
          options={STYLE_CHOICES}
          onChange={(v) => HOME_STYLES.includes(v as HomeStyle) && save.mutate({ homeStyle: v as HomeStyle })}
        />
      )}
      {sections.photos && (
        <Choice
          legend="Photos style"
          hint={
            photosStyle === 'modern'
              ? 'Photos open as a wall of whole pictures, with people’s stories along the top and tags to browse by.'
              : 'Photos open on albums, the way they always have.'
          }
          value={photosStyle ?? 'traditional'}
          options={STYLE_CHOICES}
          onChange={(v) => PHOTO_STYLES.includes(v as PhotoStyle) && save.mutate({ photosStyle: v as PhotoStyle })}
        />
      )}
      {sections.moments && (
        <Choice
          legend="Moments style"
          hint={
            momentsStyle === 'modern'
              ? 'Moments open as a feed or cards, with All, Circles and People tabs, Now or Then in Filter, and what’s happening now along the top.'
              : 'Moments open as the feed, cards or list, the way they always have.'
          }
          value={momentsStyle ?? 'traditional'}
          options={STYLE_CHOICES}
          onChange={(v) => MOMENT_STYLES.includes(v as MomentStyle) && save.mutate({ momentsStyle: v as MomentStyle })}
        />
      )}
      {sections.recipes && (
        <Choice
          legend="Recipes style"
          hint={
            recipesStyle === 'modern'
              ? 'Recipes open as a wall of whole dishes that turn over to their cards, with each kitchen along the top.'
              : 'Recipes open on their books, the way they always have.'
          }
          value={recipesStyle ?? 'traditional'}
          options={STYLE_CHOICES}
          onChange={(v) => RECIPE_STYLES.includes(v as RecipeStyle) && save.mutate({ recipesStyle: v as RecipeStyle })}
        />
      )}
      {sections.calendar && (
        <Choice
          legend="Calendar style"
          hint={
            calendarStyle === 'modern'
              ? 'The Calendar opens on who’s coming up in rings, then each day that has something on it as a card.'
              : 'The Calendar opens on the month as a grid, the way it always has.'
          }
          value={calendarStyle ?? 'traditional'}
          options={STYLE_CHOICES}
          onChange={(v) =>
            CALENDAR_STYLES.includes(v as CalendarStyle) && save.mutate({ calendarStyle: v as CalendarStyle })
          }
        />
      )}
      <Choice
        legend="Who can invite people to a circle"
        hint="You always can."
        value={invites}
        options={INVITE_CHOICES}
        onChange={(v) => INVITE_MODES.includes(v as InviteMode) && save.mutate({ invites: v as InviteMode })}
      />
      {invites === 'chosen' && (
        <fieldset className="grid min-w-0 gap-2">
          <legend className="mb-2 text-sm font-medium">People who can invite</legend>
          {accounts.length === 0 ? (
            <p className="text-sub text-muted-foreground">No one else has an account yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-border/70">
              {accounts.map((account) => (
                <li key={account.id} className="flex items-center justify-between gap-3 py-2">
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{account.name || account.email}</span>
                    <span className="truncate text-sub text-muted-foreground">{account.email}</span>
                  </span>
                  <label className="flex min-h-11 shrink-0 cursor-pointer items-center rounded-full border border-input px-4 text-sm transition-colors has-checked:border-brand has-checked:bg-brand has-checked:text-brand-foreground has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50">
                    <input
                      type="checkbox"
                      checked={account.canInvite}
                      onChange={(e) => grant.mutate({ id: account.id, canInvite: e.target.checked })}
                      aria-label={`${account.name || account.email} can invite`}
                      className="sr-only"
                    />
                    Can invite
                  </label>
                </li>
              ))}
            </ul>
          )}
          <FormError error={grant.error} />
        </fieldset>
      )}
      <div className="flex flex-col gap-1.5">
        <h3 className="text-sm font-medium">Colours</h3>
        <p className="text-sub text-muted-foreground">
          Pick every colour on the site, in light and dark:{' '}
          <Link to="/colours" className="text-brand underline underline-offset-4">
            Colours
          </Link>
          .
        </p>
      </div>
      <p className="text-sub text-muted-foreground">
        Who may create an account at all is on your{' '}
        <Link to="/invites" className="text-brand underline underline-offset-4">
          invite list
        </Link>
        .
      </p>
      <TestPeople />
    </section>
  )
}
